/* eslint-disable react/prop-types */
import React, {
  PropsWithChildren,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useBeforeUnload } from 'react-use'
import { Box, IconButton, Typography } from '@mui/material'
import { Button, Icon } from 'src/components/atoms'
import BackdropLoading from 'src/components/molecules/BackdropLoading'
import BreadcrumbsPipe, { Path } from 'src/components/molecules/BreadcrumbsPipe'
import CenteringLoading from 'src/components/molecules/CenteringLoading'
import InputModal from 'src/components/molecules/InputModal'
import Modal from 'src/components/molecules/Modal'
import { Option } from 'src/components/molecules/SelectWithGroupOption'
import { EditOCRFormat as EditOCRFormatPayload } from 'src/slices/editCalculations/editCalculationsSlice'
import { CalculationItem, CalculationDSL } from 'src/slices/services/api'
import { isEqual } from 'src/utils/array'
import { isNumber } from 'src/utils/number'
import EditCalculationDSLTable, {
  BodyRow,
} from '../components/EditCalculationDSLTable'
import EditCalculationDSLFunctions, {
  RowFieldValue,
  CELL_SET_COUNT,
  generateCellSet,
} from './function'

import style from './styles.module.scss'

export type EditCalculationDSLProps = {
  breadcrumbs: Path[]
  calculationItems?: CalculationItem[]
  dsls?: CalculationDSL[]
  editOCRFormats: EditOCRFormatPayload[]
  isSubmitting?: boolean // 算出ロジック変更 API のレスポンス待ちかどうか
  onClickGoForward: (convertedDSLs: string[]) => void
  onClickGoBack: () => void
  onClickConfirmClose: () => void
}

const EditCalculationDSL: React.StyledFC<EditCalculationDSLProps> = ({
  breadcrumbs,
  calculationItems,
  editOCRFormats,
  dsls,
  isSubmitting,
  onClickGoForward,
  onClickGoBack,
  onClickConfirmClose,
}: PropsWithChildren<EditCalculationDSLProps>) => {
  const [isLoading, setIsLoading] = useState(true)
  const [initialValues, setInitialValues] = useState<
    (string | number | undefined)[]
  >([])

  const formMethods = useForm<{
    rows: RowFieldValue[][]
    inputNumber: string
  }>()

  useBeforeUnload(() => {
    const currentValues =
      formMethods
        .getValues()
        .rows?.flatMap((rows) =>
          rows.flatMap((cellSet) => Object.values(cellSet))
        ) ?? []
    return !isEqual(initialValues, currentValues)
    // NOTE: 第2引数はブラウザのページ離脱確認ダイアログを表示するために必要（カスタムメッセージは表示されず、ブラウザ固有の定型文が表示される）
  }, 'Alert')

  const inputName = 'inputNumber'
  // NOTE: selectBox が展開された時に、その値を inputModal へ渡す
  const [selectedInputNumberValue, setSelectedInputNumberValue] = useState<
    number | undefined
  >()

  const [inputCellPosition, setInputCellPosition] = useState<{
    rowIndex: number
    columnSetIndex: number
  }>({ rowIndex: 0, columnSetIndex: 0 })

  const [showWarningModal, setShowWarningModal] = useState(false)
  const [showInputModal, setShowInputModal] = useState(false)
  // NOTE: 初期の header column から、いくつ cell を増やすかを管理
  const [addedCellSetCount, setAddedCellSetCount] = useState(0)
  const [rows, setRows] = useState<BodyRow[]>([])

  // NOTE: 初回データ読み込み完了後にフォームの初期値を保存し、離脱確認の比較基準とする
  // TODO: react-hook-form の Controller 登録完了前に getValues() が空を返す可能性がある。watch や setTimeout でフォーム描画完了を待つ方式への改善を検討する
  const initialValuesSetRef = useRef(false)
  useEffect(() => {
    if (!isLoading && !initialValuesSetRef.current) {
      const values = formMethods
        .getValues()
        .rows?.flatMap((rowCells) =>
          rowCells.flatMap((cellSet) => Object.values(cellSet))
        )
      if (values && values.length > 0) {
        setInitialValues(values)
        initialValuesSetRef.current = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formMethods は useForm の戻り値で参照が安定しているため除外
  }, [isLoading, rows])

  // utils
  const getInputNumberName =
    useCallback((): `rows.${number}.${number}.value` => {
      return `rows.${inputCellPosition.rowIndex}.${inputCellPosition.columnSetIndex}.value`
    }, [inputCellPosition])

  // calculationItemsの長さを追跡して、変更時のみrowsを更新
  const prevCalculationItemsLengthRef = useRef<number>(0)

  // TODO: editOCRFormats が依存配列にあるが、needsUpdate は calculationItems.length しか見ていないため、editOCRFormats のみ変化した場合に rows が再構築されない。editOCRFormats の変化を検知して再構築するか、依存配列から外すか設計判断が必要
  useEffect(() => {
    if (!dsls || !calculationItems) {
      prevCalculationItemsLengthRef.current = 0
      return
    }
    // 初回または項目数が変わった場合のみ更新
    const needsUpdate =
      prevCalculationItemsLengthRef.current !== calculationItems.length
    if (!needsUpdate) {
      if (isLoading) setIsLoading(false)
      return
    }
    prevCalculationItemsLengthRef.current = calculationItems.length

    const restoredEditCalculationDSLs =
      EditCalculationDSLFunctions.convertEditDataToFormValues(
        dsls,
        calculationItems,
        editOCRFormats
      )

    const tableData = EditCalculationDSLFunctions.tableData(
      calculationItems,
      editOCRFormats,
      restoredEditCalculationDSLs
    )

    setRows(tableData.rows)
    setAddedCellSetCount(tableData.maxCellSetCount - 1)
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isLoading は依存配列に含めると二重実行されるため意図的に除外
  }, [calculationItems, dsls, editOCRFormats])

  // event handler
  const handleClickClosePage = () => {
    setShowWarningModal(true)
  }

  const handleClickAddCell = (rowIndex: number) => {
    if (
      !calculationItems ||
      rowIndex >= calculationItems.length ||
      rowIndex >= rows.length
    ) {
      return
    }

    const { cells } = rows[rowIndex]

    if (cells.length - CELL_SET_COUNT === addedCellSetCount * CELL_SET_COUNT) {
      setAddedCellSetCount((prev) => prev + 1)
    }

    const cellIndex = rows[rowIndex].cells.length

    const calculationId = calculationItems[rowIndex].id

    const updatedRow: BodyRow = {
      ...rows[rowIndex],
      cells: [
        ...rows[rowIndex].cells,
        ...generateCellSet(
          rowIndex,
          cellIndex,
          calculationItems ?? [],
          editOCRFormats,
          calculationId
        ),
      ],
      disabledAddButton: true,
    }

    setRows((prevRows) =>
      prevRows.map((row, index) => (index === rowIndex ? updatedRow : row))
    )
  }

  const handleClickCancelClose = () => {
    setShowWarningModal(false)
  }

  const handleClickConfirmInput = () => {
    // @ts-ignore
    formMethods.setValue(getInputNumberName(), formMethods.getValues(inputName))
    setShowInputModal(false)
  }

  const handleClickCloseInput = () => {
    // @ts-ignore
    formMethods.setValue(getInputNumberName(), selectedInputNumberValue)
    setShowInputModal(false)
  }

  const handleOpenSelect = (rowIndex: number, columnIndex: number) => {
    // セレクトボックスを展開した際に、数値が入っていた場合、その値を保持する
    setInputCellPosition({
      rowIndex,
      columnSetIndex: Math.floor(columnIndex / CELL_SET_COUNT),
    })
  }

  const handleChangeSelect = (
    option: Option,
    rowIndex: number,
    cellIndex: number
  ) => {
    const formValue = formMethods.getValues(getInputNumberName())
    if (isNumber(formValue)) {
      setSelectedInputNumberValue(Number(formValue))
    } else {
      setSelectedInputNumberValue(undefined)
    }
    if (option.value === 'inputNumber') {
      setShowInputModal(true)
    }
    // NOTE: 最後のセル(＝四則演算子)が空の場合は、次の項目を選択することがないので、プラスボタンを非活性
    const isLastCell = rows[rowIndex].cells.length - 1 === cellIndex
    if (isLastCell) {
      setRows((prevRows) =>
        prevRows.map((row, index) =>
          index === rowIndex
            ? { ...row, disabledAddButton: !option.value }
            : row
        )
      )
    }
  }

  const handleClickGoForward = async () => {
    const convertedDSLs = EditCalculationDSLFunctions.convertFormValuesToDSLs(
      formMethods.getValues()
    )
    onClickGoForward(convertedDSLs)
  }

  const renderContent = () => {
    return (
      <Box className={style.content}>
        <Typography className={style.title} variant="h1">
          {`最後に売上内容の\n算出ロジックを設定しましょう。`}
        </Typography>
        <EditCalculationDSLTable
          className={style.table}
          columns={EditCalculationDSLFunctions.columns(addedCellSetCount)}
          rows={rows}
          onClickAdd={handleClickAddCell}
          onOpenSelect={handleOpenSelect}
          onChangeSelect={handleChangeSelect}
          valueToRenderEmpty={inputName}
        />
        <Box className={style.buttonWrapper}>
          <Button
            title="つぎへ"
            onClick={handleClickGoForward}
            color="primary"
            size="medium"
          />
          <Typography
            color="primary"
            className={style.previousTypography}
            onClick={onClickGoBack}
            component="p"
            variant="subtitle1"
          >
            前ページに戻る
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <form>
        <Box className={style.root}>
          <Box className={style.header} component="header">
            <BreadcrumbsPipe className={style.breadcrumb} paths={breadcrumbs} />
            <Box className={style.closeButtonWrapper}>
              <IconButton
                className={style.closeButton}
                onClick={handleClickClosePage}
                size="large"
              >
                <Icon icon="close" />
              </IconButton>
            </Box>
          </Box>
          <Box className={style.contentWrapper}>
            {isLoading ? <CenteringLoading /> : renderContent()}
            <Modal // `disableBackdropClick` is removed by codemod.
              // You can find more details about this breaking change in [the migration guide](https://mui.com/guides/migration-v4/#modal)
              open={showWarningModal}
              title={`今中断するとこれまでの設定内容は\n失われてしまいますがよろしいですか？`}
              agreeButtonTitle="このまま中断する"
              disagreeButtonTitle="設定に戻る"
              onClickAgree={onClickConfirmClose}
              onClickDisagree={handleClickCancelClose}
            />
            <InputModal
              open={showInputModal}
              name={inputName}
              type="number"
              defaultValue={selectedInputNumberValue}
              placeholder="数値を入力"
              confirmButtonText="決定"
              closeButtonText="設定に戻る"
              onClickConfirm={handleClickConfirmInput}
              onClose={handleClickCloseInput}
              onClickClear={() => formMethods.setValue(inputName, '')}
            />
          </Box>
          <BackdropLoading open={isSubmitting} />
        </Box>
      </form>
    </FormProvider>
  )
}

export default EditCalculationDSL
